import { AuthRoutes, PrivateRoute } from '@codezo/auth';
import {
	AuthenticationUser,
	AuthRoleEnum,
	CurrencyEnum,
	MeasurementSystemEnum,
} from '@codezo/graphql';
import { ThemeProvider } from '@codezo/theme';
import { MantineProvider } from '@mantine/core';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { PlatformLayout } from './app/layouts/PlatformLayout';
import { theme } from './theme.config';

import '@codezo/theme/src/global.css';
import '@mantine/core/styles.css';
import { useCallback } from 'react';
import { DashboardPage } from './features/dashboard';
import { NotFoundPage } from './shared/components/NotFoundPage';
import { OrganizationUserType, useAuth } from './shared/hooks/useAuth';
import './styles.css';

export default function App() {
	const { isAuthenticated, user, setUser, setAccessToken, token } = useAuth();

	const formatLoginUser = useCallback((user: AuthenticationUser): OrganizationUserType => {
		const orgUser = user.organizationUser;
		const org = orgUser?.organization;
		const orgSetting = org?.organizationSetting;

		return {
			id: user.id,
			email: user.email || '',
			role: user.role,
			organizationId: orgUser?.organizationId || '',
			organization: {
				id: orgUser?.organizationId || '',
				name: org?.name || '',
				onboarded: org?.onboarded ?? false,
				organizationSetting: {
					currency: orgSetting?.currency || CurrencyEnum.Usd,
					measurementSystem:
						orgSetting?.measurementSystem || MeasurementSystemEnum.Metric,
					timezone: orgSetting?.timezone || 'UTC',
				},
			},
			organizationUser: {
				id: orgUser?.id || '',
				firstName: orgUser?.firstName || '',
				lastName: orgUser?.lastName || '',
			},
		};
	}, []);

	return (
		<MantineProvider theme={theme} defaultColorScheme="light" forceColorScheme="light">
			<ThemeProvider>
				<Router>
					<AuthRoutes
						setUser={setUser}
						setAccessToken={setAccessToken}
						userFormatter={formatLoginUser}
						token={token}
						requiredRole={AuthRoleEnum.Manufacturer}
					/>
					<Routes>
						{/* Protected routes with main layout */}
						<Route
							path="/"
							element={
								<PrivateRoute
									isAuthenticated={isAuthenticated}
									user={user}
									requiredRole={AuthRoleEnum.Manufacturer}
								>
									<PlatformLayout />
								</PrivateRoute>
							}
						>
							<Route index element={<Navigate to="/dashboard" replace />} />
							<Route path="dashboard" element={<DashboardPage />} />
							<Route path="settings" element={<div>Settings Page Coming Soon</div>} />
						</Route>

						{/* 404 - Catch all unmatched routes */}
						<Route path="*" element={<NotFoundPage />} />
					</Routes>
				</Router>
			</ThemeProvider>
		</MantineProvider>
	);
}
